<script setup>
import { computed, defineModel, h, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { Delete03Icon } from '@hugeicons/core-free-icons';
import Button from 'next/button/Button.vue';
import HugeIcon from 'dashboard/components-next/icon/HugeIcon.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import FilterSelect from './inputs/FilterSelect.vue';
import MultiSelect from './inputs/MultiSelect.vue';
import SingleSelect from './inputs/SingleSelect.vue';
import WorkflowInboxMultiSelect from 'dashboard/routes/dashboard/settings/automation/WorkflowInboxMultiSelect.vue';

import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { validateSingleFilter } from 'dashboard/helper/validations.js';

const { filterTypes, layout } = defineProps({
  showQueryOperator: { type: Boolean, default: false },
  filterTypes: { type: Array, required: true },
  hideAttributeSelect: { type: Boolean, default: false },
  hideFilterOperatorSelect: { type: Boolean, default: false },
  hideRemoveButton: { type: Boolean, default: false },
  layout: {
    type: String,
    default: 'inline',
    validator: value => ['inline', 'workflow'].includes(value),
  },
});

const emit = defineEmits(['remove']);

// filterTypes: import('vue').ComputedRef<FilterType[]>
const WORKFLOW_FILTER_TRIGGER_CLASS =
  'flex h-9 w-full min-w-0 items-center justify-between gap-2 rounded-lg border border-n-weak bg-n-slate-2/70 px-3 py-2 text-sm text-n-slate-12 dark:border-n-strong dark:bg-n-slate-3/30';

const isWorkflowLayout = computed(() => layout === 'workflow');

const { t } = useI18n();
const showErrors = ref(false);

const attributeKey = defineModel('attributeKey', {
  type: String,
  required: true,
});

const values = defineModel('values', {
  type: [String, Number, Array, Object],
  required: true,
});

const filterOperator = defineModel('filterOperator', {
  type: String,
  required: true,
});

const queryOperator = defineModel('queryOperator', {
  type: String,
  required: false,
  default: undefined,
  validator: value => ['and', 'or'].includes(value),
});

const getFilterFromFilterTypes = key =>
  filterTypes.find(filterObj => filterObj.attributeKey === key);

const currentFilter = computed(() =>
  getFilterFromFilterTypes(attributeKey.value)
);

const getOperator = (filter, selectedOperator) => {
  const operatorFromOptions = filter?.filterOperators?.find(
    operator => operator.value === selectedOperator
  );

  if (!operatorFromOptions) {
    return filter?.filterOperators?.[0];
  }

  return operatorFromOptions;
};

const currentOperator = computed(() =>
  getOperator(currentFilter.value, filterOperator.value)
);

const getInputType = (operator, filter) =>
  operator.inputOverride ?? filter.inputType;

const inputType = computed(() =>
  getInputType(currentOperator.value, currentFilter.value)
);

const isWorkflowInboxFilter = computed(
  () => isWorkflowLayout.value && attributeKey.value === 'inbox_id'
);

const queryOperatorOptions = computed(() => {
  return [
    {
      label: t(`FILTER.QUERY_DROPDOWN_LABELS.AND`),
      value: 'and',
      icon: h('span', { class: 'i-lucide-ampersands !text-n-blue-11' }),
    },
    {
      label: t(`FILTER.QUERY_DROPDOWN_LABELS.OR`),
      value: 'or',
      icon: h('span', { class: 'i-woot-logic-or !text-n-blue-11' }),
    },
  ];
});

const booleanOptions = computed(() => [
  { id: true, name: t('FILTER.ATTRIBUTE_LABELS.TRUE') },
  { id: false, name: t('FILTER.ATTRIBUTE_LABELS.FALSE') },
]);

const validationError = computed(() => {
  // TOOD: Migrate validateSingleFilter to use camelcase and then remove useSnakeCase here too
  return validateSingleFilter(
    useSnakeCase({
      attributeKey: attributeKey.value,
      filterOperator: filterOperator.value,
      values: values.value,
    })
  );
});

const inputFieldType = computed(() => {
  if (inputType.value === 'date') return 'date';
  if (inputType.value === 'number') return 'number';
  return 'text';
});

const resetModelOnAttributeKeyChange = newAttributeKey => {
  /**
   * Resets the filter values and operator when the attribute key changes. This ensures that
   * the values and operator remain compatible with the new attribute type. For example,
   * switching from a text field to a multi-select should reset the value from '' (empty string)
   * to an empty array.
   */
  const filter = getFilterFromFilterTypes(newAttributeKey);
  const newOperator = getOperator(filter, filterOperator.value);
  const newInputType = getInputType(newOperator, filter);
  if (newInputType === 'multiSelect') {
    values.value = [];
  } else if (['searchSelect', 'booleanSelect'].includes(newInputType)) {
    values.value = {};
  } else {
    values.value = '';
  }
  filterOperator.value = newOperator.value;
};

watch([attributeKey, values, filterOperator], () => {
  showErrors.value = false;
});

const validate = () => {
  showErrors.value = true;
  return !validationError.value;
};

const resetValidation = () => {
  showErrors.value = false;
};

defineExpose({ validate, resetValidation });
</script>

<template>
  <li class="list-none">
    <div
      v-if="isWorkflowLayout"
      class="flex flex-col gap-2"
      :class="{ 'animate-wiggle': showErrors && validationError }"
    >
      <FilterSelect
        v-if="showQueryOperator"
        v-model="queryOperator"
        variant="faded"
        hide-icon
        class="w-full max-w-[6rem] text-sm"
        :options="queryOperatorOptions"
      />
      <div
        class="grid items-center gap-x-2 gap-y-2"
        :class="
          hideAttributeSelect && hideFilterOperatorSelect && hideRemoveButton
            ? 'grid-cols-[minmax(0,1fr)]'
            : 'grid-cols-[minmax(0,1fr)_auto_22px]'
        "
      >
        <FilterSelect
          v-if="!hideAttributeSelect"
          v-model="attributeKey"
          variant="faded"
          :options="filterTypes"
          class="min-w-0"
          @update:model-value="resetModelOnAttributeKeyChange"
        >
          <template #trigger="{ toggle }">
            <button
              type="button"
              :class="WORKFLOW_FILTER_TRIGGER_CLASS"
              @click="toggle"
            >
              <span class="truncate">{{
                currentFilter?.label || t('COMBOBOX.PLACEHOLDER')
              }}</span>
              <span
                class="i-lucide-chevron-down size-4 shrink-0 text-n-slate-11"
                aria-hidden="true"
              />
            </button>
          </template>
        </FilterSelect>
        <FilterSelect
          v-if="!hideFilterOperatorSelect"
          v-model="filterOperator"
          variant="ghost"
          class="shrink-0"
          :options="currentFilter?.filterOperators"
        >
          <template #trigger="{ toggle }">
            <button
              type="button"
              class="w-auto min-w-[7.5rem]"
              :class="[WORKFLOW_FILTER_TRIGGER_CLASS]"
              @click="toggle"
            >
              <span class="truncate">{{ currentOperator?.label }}</span>
              <span
                class="i-lucide-chevron-down size-4 shrink-0 text-n-slate-11"
                aria-hidden="true"
              />
            </button>
          </template>
        </FilterSelect>
        <button
          v-if="!hideRemoveButton"
          type="button"
          class="flex size-[22px] shrink-0 items-center justify-center border-0 bg-transparent p-0 text-n-slate-11 outline-none transition-opacity hover:opacity-80"
          :aria-label="t('AUTOMATION.FLOW.TRIGGER_CONDITIONS.REMOVE_FILTER')"
          @click.stop="emit('remove')"
        >
          <HugeIcon
            :icon="Delete03Icon"
            :size="22"
            class="pointer-events-none shrink-0"
          />
        </button>
        <div v-if="currentOperator?.hasInput" class="col-span-1 min-w-0">
          <WorkflowInboxMultiSelect
            v-if="isWorkflowInboxFilter && inputType === 'multiSelect'"
            v-model="values"
            v-model:filter-operator="filterOperator"
            :has-error="showErrors && Boolean(validationError)"
          />
          <MultiSelect
            v-else-if="inputType === 'multiSelect'"
            v-model="values"
            class="w-full"
            :options="currentFilter.options"
            dropdown-max-height="max-h-72"
          />
          <SingleSelect
            v-else-if="inputType === 'searchSelect'"
            v-model="values"
            class="w-full"
            :options="currentFilter.options"
            dropdown-max-height="max-h-64"
          />
          <SingleSelect
            v-else-if="inputType === 'booleanSelect'"
            v-model="values"
            class="w-full"
            disable-search
            :options="booleanOptions"
          />
          <Input
            v-else
            v-model="values"
            :type="inputFieldType"
            class="w-full [&>input]:h-9 [&>input]:rounded-lg [&>input]:border-n-weak [&>input]:bg-n-slate-2/70 [&>input]:py-2 dark:[&>input]:border-n-strong dark:[&>input]:bg-n-slate-3/30"
            :placeholder="t('FILTER.INPUT_PLACEHOLDER')"
          />
        </div>
      </div>
      <span v-if="showErrors && validationError" class="text-sm text-n-ruby-11">
        {{ t(`FILTER.ERRORS.${validationError}`) }}
      </span>
    </div>

    <template v-else>
      <div
        class="flex items-center gap-2 rounded-md"
        :class="{
          'animate-wiggle': showErrors && validationError,
        }"
      >
        <FilterSelect
          v-if="showQueryOperator"
          v-model="queryOperator"
          variant="faded"
          hide-icon
          class="text-sm"
          :options="queryOperatorOptions"
        />
        <FilterSelect
          v-model="attributeKey"
          variant="faded"
          :options="filterTypes"
          @update:model-value="resetModelOnAttributeKeyChange"
        />
        <FilterSelect
          v-model="filterOperator"
          variant="ghost"
          :options="currentFilter?.filterOperators"
        />
        <template v-if="currentOperator?.hasInput">
          <MultiSelect
            v-if="inputType === 'multiSelect'"
            v-model="values"
            :options="currentFilter.options"
            dropdown-max-height="max-h-72"
          />
          <SingleSelect
            v-else-if="inputType === 'searchSelect'"
            v-model="values"
            :options="currentFilter.options"
            dropdown-max-height="max-h-64"
          />
          <SingleSelect
            v-else-if="inputType === 'booleanSelect'"
            v-model="values"
            disable-search
            :options="booleanOptions"
          />
          <Input
            v-else
            v-model="values"
            :type="inputFieldType"
            class="[&>input]:h-8 [&>input]:py-1.5 [&>input]:outline-offset-0"
            :placeholder="t('FILTER.INPUT_PLACEHOLDER')"
          />
        </template>
        <Button
          sm
          solid
          slate
          icon="i-lucide-trash"
          class="flex-shrink-0"
          @click.stop="emit('remove')"
        />
      </div>
      <span v-if="showErrors && validationError" class="text-sm text-n-ruby-11">
        {{ t(`FILTER.ERRORS.${validationError}`) }}
      </span>
    </template>
  </li>
</template>
